import { urlStartsWithHTTPProtocol } from '@chegg-wt/core/utils/url'
import { CITATIONS_PER_PAGE } from '../constants/projects'
import { CreateCitationInput } from '../globalTypes'
import { Citationcount } from '../queries/Citationcount'
import { sortOptions } from '../state/bibliography/constants'
import { sortOptionsList } from '../state/projects/constants'
import { Citation } from '../types/redux-citation-api'
import { Contributor } from '../types/core'

export const getCitationTitle = (citation: Citation): string => {
  if (citation.source && citation[citation.source] && citation[citation.source].title) {
    return citation[citation.source].title
  }
  if (citation.website && citation.website.title) {
    return citation.website.title
  }
  if (citation.pubonline && citation.pubonline.title) {
    return citation.pubonline.title
  }
  if (citation.pubnonperiodical && citation.pubnonperiodical.title) {
    return citation.pubnonperiodical.title
  }
  return ''
}

export const replaceCitationTitle = (citation: Citation, title: string): Citation => {
  const newCitation = citation // Just to avoid lint warning. Note this method mutates citation
  if (citation.source && citation[citation.source] && citation[citation.source].title) {
    newCitation[citation.source].title = title
  } else if (citation.website && citation.website.title) {
    newCitation.website.title = title
  } else if (citation.pubonline && citation.pubonline.title) {
    newCitation.pubonline.title = title
  } else if (citation.pubnonperiodical && citation.pubnonperiodical.title) {
    newCitation.pubnonperiodical.title = title
  }
  return newCitation
}

export const getCitationYear = (citation: Citation, style: string): string => {
  if (style.startsWith('mla')) return ''
  if (!citation || !citation.pubtype || !citation.pubtype.main) return undefined

  const type = citation.pubtype.main

  if (!citation[type]) return undefined
  return citation[type].year
}

const formatContribs = (contribs: Contributor[]) =>
  contribs
    .filter((contributor) => contributor.last !== '')
    .map((contributor) => contributor.last)

const addSeparator = (contributor: string, id: number, array: any[]) =>
  id === array.length - 1 ? `& ${contributor}` : contributor

export const getContributors = (citation: Citation, style: string = '') => {
  const formattedContributors = citation.contributors ? citation.contributors : []
  let contributors = formattedContributors
  const chapterAuthor = contributors.find(
    (contributor) => contributor.function === 'section_author'
  )
  if (chapterAuthor && style === 'apa') {
    contributors = [chapterAuthor]
  }
  const separator = style.startsWith('mla') ? 'and ' : '& '
  const len = contributors.length

  if (len === 0) return ''
  if (len === 1 || len === 2) {
    return formatContribs(contributors).join(` ${separator}`)
  }
  if (style === 'apa' && len <= 5) {
    return formatContribs(contributors).map(addSeparator).join(', ')
  }
  return `${contributors[0].last} et al.`
}

export const getCitationIdentifier = (citation: Citation, style: string): string => {
  if (!style.startsWith('mla')) return ''
  if (citation.pubnonperiodical) {
    const { start = '', end = '' } = citation.pubnonperiodical
    return `${start}${start && end && '-'}${end}`
  }
  return ''
}

export const getStyleUrl = (style: string) => {
  if (!style.startsWith('mla') || style !== 'apa') {
    return ''
  }
  const value = style === 'apa' ? style : 'mla'
  return `http://content.easybib.com/citation-guides/${value}-format/how-to-cite-a-paranthetical-citations-${value}/`
}

export const cleanupCitation = (citation) => {
  const whitelist = [
    'source',
    'pubtype',
    'contributors',
    'doi',
    'annotation',
    'email',
    'issn',
    'keywords',
    'oclc',
    'npnd',
  ]

  const cleanCitation = {
    source: {},
    pubtype: {},
  }

  whitelist.push(citation.source)

  if (citation.pubtype) {
    const { suffix, main } = citation.pubtype
    if (suffix) {
      whitelist.push(suffix)
    }
    if (main) {
      whitelist.push(main)
    }
  }

  whitelist.forEach((allowedKey) => {
    if (allowedKey in citation) {
      cleanCitation[allowedKey] = citation[allowedKey]
    }
  })

  // if you try to post this object to the backend with
  // empty values for 'suffix' or 'main', it will
  // explode with a 400, so remove them if they are empty

  if (citation.pubtype) {
    const { suffix, main, ...pubtype } = citation.pubtype

    if (suffix) {
      pubtype.suffix = suffix
    }

    if (main) {
      pubtype.main = main
    }

    cleanCitation.pubtype = pubtype
  }

  return cleanCitation
}

export const citationHasValidSourceLink = (citation: Citation) => {
  // This is where the url will be contained, if it's part of the citation
  const { pubonline } = citation
  if (pubonline == null) return false

  const { url } = pubonline
  if (url == null) return false

  return urlStartsWithHTTPProtocol(url)
}

export const flattenData = ({ data, ...rest }) => ({ ...rest, ...data })

export const getCitationCount = (citationsCountData: Citationcount) =>
  citationsCountData?.citationCount?.count ?? 0

export const getShowPagination = (citationCount: number) =>
  citationCount > CITATIONS_PER_PAGE

export const getMaxCitationPages = (citationCount: number) =>
  Math.ceil(citationCount / CITATIONS_PER_PAGE)

export const getSortOptions = (citationCount: number) => {
  if (citationCount < CITATIONS_PER_PAGE) {
    return sortOptions
  }
  const [, ...rest] = sortOptions
  return rest
}

export const getSortOptionsList = (citationCount: number) => {
  if (citationCount < CITATIONS_PER_PAGE) {
    return sortOptionsList
  }
  const [, ...rest] = sortOptionsList
  return rest
}

export const getCreateCitationData = (
  citation,
  withAnnotation: boolean
): CreateCitationInput => {
  const { contributors, annotation, ...strippedCitation } = citation
  const citationData: CreateCitationInput = { data: strippedCitation }
  if (citation.pubtype?.main) {
    citationData.pubType = citation.pubtype.main
  }
  if (citation.source) {
    citationData.sourceType = citation.source
  }
  if (citation.annotation?.text && withAnnotation) {
    citationData.annotation = citation.annotation.text
  }
  if (citation.contributors) {
    citationData.contributors = citation.contributors
  }
  return citationData
}
